import { StyledButton, StyledLabel } from '@selfserve/react-ui';
import styled from '@emotion/styled';
import { Tooltip, Typography } from '@kep-ui-kit/ui-components';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { FormikProps } from 'formik';
import { FormData, SpeakerData } from '../feature/webinar-creator-wizard';
import { Edit24Icon } from '@kep-ui-kit/ui-icons';
import MarketingInfoContext from '../../../../react-feature-marketing-info/src/lib/utils/markerting-info-context';

interface Props {
  label: string;
  editLabel: string;
  tooltipText: string;
  formik: FormikProps<FormData> | FormikProps<SpeakerData>;
  valueName: string;
  withoutBackground?: boolean;
  type?: string;
  disabled?: boolean;
  callback?: (isLoading: boolean) => void;
  eventName?: string;
}

export const StyledUploadContainer = styled.div(
  ({ theme, type }: { theme?: any; type?: string }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...(type !== 'overlay' && {
      '& .kepuikit-button-root': {
        width: 'fit-content',
        padding: '8px 8px 8px 4px',
      },
    }),
  })
);

const StyledTooltip = styled(Tooltip)(
  ({ theme }: { theme?: any }) => ({
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(24),
    height: '32px',
  }),
  { label: 'StyledTooltip' }
);

const StyledImageContainer = styled.div(
  ({
    type,
    withoutBackground,
  }: {
    type: string;
    withoutBackground?: boolean;
  }) => ({
    ...(type === 'default' && {
      width: `200px`,
      height: `120px`,
    }),
    ...(type === 'small' && {
      width: `120px`,
      height: `36px`,
    }),
    ...(type === 'overlay' && {
      width: `168px`,
      height: `168px`,
    }),
    borderRadius: '8px',
    ...(!withoutBackground && {
      background:
        'repeating-conic-gradient(#222222 0% 25%, #444444 0% 50%) 50%/20px 20px',
    }),
    '&.overlay': {},
  })
);

const StyledImage = styled.div(
  ({
    type,
    theme,
    placeholder,
    uploadedImage,
  }: {
    type?: string;
    theme?: any;
    placeholder: string;
    uploadedImage?: string;
  }) => ({
    backgroundImage:
      'url("' + (uploadedImage ? uploadedImage : placeholder) + '")',
    ...(type === 'default' && {
      width: `200px`,
      height: `120px`,
    }),
    ...(type === 'small' && {
      width: `120px`,
      height: `36px`,
    }),
    ...(type === 'overlay' && {
      width: `168px`,
      height: `168px`,
    }),
    borderRadius: '8px',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    '&.overlay': {
      display: 'flex',
      flexDirection: 'column',
      '&.unloaded': {
        backgroundImage: 'url("assets/speaker_loader_bg.svg")',
        backgroundColor: '#F4F4F4',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      },

      '&.loaded': {
        alignItems: 'end',
        justifyContent: 'flex-end',
        padding: '8px',

        '& button': {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: theme.palette.common.white,

          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 1)',
          },
        },
      },

      '& button': {
        marginBottom: '0px',
      },
    },
  })
);

const StyledUploading = styled('div')(
  ({ type }: { type?: string; width?: number; height?: number }) => ({
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    ...(type === 'default' && {
      width: `200px`,
      height: `120px`,
    }),
    ...(type === 'small' && {
      width: `120px`,
      height: `36px`,
      flexDirection: 'row',
      gap: '2px',
    }),
    ...(type === 'overlay' && {
      width: `168px`,
      height: `168px`,
    }),
    backgroundColor: '#00A3FF',
  }),
  { label: 'StyledUploading' }
);

export const StyledPercentageMask = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 0,
  height: '100%',
  background: 'rgba(0, 0, 0, 0.2)',
});

export const StyledUploadingText = styled(Typography)(
  ({ theme, type }: { theme?: any; type?: string }) => ({
    color: theme.palette.common.white,
    fontWeight: '700',
    ...(type === 'default' && {
      fontWeight: '400',
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(18),
    }),
    ...(type === 'small' && {
      fontSize: theme.typography.pxToRem(12),
      lineHeight: theme.typography.pxToRem(14),
    }),
    ...(type === 'overlay' && {
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(21),
    }),
    zIndex: 1,
  }),
  { label: 'StyledUploadingText' }
);

export const StyledPercentage = styled(Typography)(
  ({ theme, type }: { theme?: any; type?: string }) => ({
    color: theme.palette.common.white,
    fontWeight: '700',
    ...(type === 'default' && {
      fontSize: theme.typography.pxToRem(24),
      lineHeight: theme.typography.pxToRem(26),
      marginBottom: '4px',
    }),
    ...(type === 'small' && {
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(16),
    }),
    ...(type === 'overlay' && {
      fontSize: theme.typography.pxToRem(32),
      lineHeight: theme.typography.pxToRem(34),
      marginBottom: '8px',
    }),
    zIndex: 1,
  }),
  { label: 'StyledPercentage' }
);

export const UploadImageButton = styled(StyledButton)(() => ({
  fontWeight: '600',
  marginTop: '4px',
  fontFamily: 'Lato',
}));

const mockProgressInterval = 250;
const mockProgressStep = 5;

export const ImageUploader = ({
  label,
  editLabel,
  tooltipText,
  formik,
  valueName,
  withoutBackground,
  type = 'default',
  disabled = false,
  callback,
  eventName,
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const { values, setFieldValue } = formik;
  const tempImageStorage = useRef<string | null>(null);
  const {
    open,
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
    },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
  });

  const handleChange = useCallback(
    (value: string) => setFieldValue(valueName, value),
    [valueName, setFieldValue]
  );

  useEffect(() => {
    if (acceptedFiles.length !== 1) {
      return;
    }
    const file: FileWithPath = acceptedFiles[0];
    if (!file.path) {
      return;
    }
    setIsLoading(true);
    if (callback) callback(true);
    tempImageStorage.current = URL.createObjectURL(file);
  }, [acceptedFiles, setIsLoading]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const getNextPercentage = (current: number): number => {
      return current + mockProgressStep;
    };

    const interval = setInterval(() => {
      setPercentage((prev: number) => {
        const nextProgress = getNextPercentage(prev);
        if (nextProgress >= 100) {
          handleChange(tempImageStorage.current as string);
          setIsLoading(false);
          if (callback) callback(false);
          return 0;
        }
        return nextProgress;
      });
    }, mockProgressInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isLoading, handleChange]);

  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);
  const handleOnClick = useCallback(() => {
    eventName && raiseAnalyticEventsRequest(eventName);
    open();
  }, [open]);

  return (
    <StyledUploadContainer type={type}>
      <StyledLabel>{label}</StyledLabel>
      {isLoading ? (
        <StyledUploading type={type}>
          <StyledPercentageMask
            style={{
              width: `${percentage}%`,
            }}
          />
          <>
            <StyledPercentage
              variant={'body1'}
              color={'ghostwhite'}
              type={type}
            >
              {percentage}%
            </StyledPercentage>
            <StyledUploadingText
              variant={'body1'}
              color={'ghostwhite'}
              type={type}
            >
              {t('webinar-wizard.uploading')}
            </StyledUploadingText>
          </>
        </StyledUploading>
      ) : (
        <StyledImageContainer
          withoutBackground={withoutBackground}
          {...getRootProps({ isDragActive, isDragAccept, isDragReject })}
          type={type}
        >
          <input {...getInputProps()} />
          <StyledImage
            className={
              type +
              ' ' +
              (!values[valueName as keyof (FormData | SpeakerData)]
                ? 'unloaded'
                : 'loaded')
            }
            type={type}
            placeholder={
              values[valueName as keyof (FormData | SpeakerData)] as string
            }
          >
            {type === 'overlay' &&
              !values[valueName as keyof (FormData | SpeakerData)] && (
                <>
                  {tooltipText}
                  <StyledButton
                    variant="borderless"
                    isSmall
                    onClick={handleOnClick}
                    disabled={disabled}
                  >
                    {editLabel}
                  </StyledButton>
                </>
              )}

            {type === 'overlay' &&
              values[valueName as keyof (FormData | SpeakerData)] && (
                <StyledButton
                  variant="borderless"
                  isSmall
                  onClick={handleOnClick}
                  disabled={disabled}
                >
                  {editLabel}
                </StyledButton>
              )}
          </StyledImage>
        </StyledImageContainer>
      )}
      {type !== 'overlay' && (
        <StyledTooltip title={tooltipText} placement={'right'}>
          <UploadImageButton
            variant="borderless"
            isSmall
            onClick={handleOnClick}
            disabled={disabled}
          >
            <Edit24Icon />
            {editLabel}
          </UploadImageButton>
        </StyledTooltip>
      )}
    </StyledUploadContainer>
  );
};
